import React from 'react';
import Navigation from './Navigation';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import LegalFooter from './shared/LegalFooter';
import { 
  Cookie,
  Settings,
  Eye,
  FileText,
  Shield,
  Globe,
  Target,
  Clock,
  HelpCircle,
  Mail,
  CheckCircle,
  Zap,
} from 'lucide-react';

const CookiePage = () => {
  const sections = [
    {
      id: "what-is-cookie",
      title: "What is a Cookie?",
      icon: Cookie,
      content: "A cookie is a small piece of data that a website stores on your device when you visit. It typically contains information about the website itself, a unique identifier that allows the site to recognise your web browser when you return, additional data that serves the cookie's purpose, and the lifespan of the cookie itself.",
      additional: "Cookies are used to enable certain features (e.g., logging in), track site usage (e.g., analytics), store your user settings (e.g., time zone, notification preferences), and to personalise your content (e.g., advertising, language).",
      list: [
        "First-party cookies: Cookies set by the website you are visiting, which track your activity only on that particular site.",
        "Third-party cookies: Cookies set by other sites or companies, which may track you on other websites that use the same third-party service."
      ]
    },
    {
      id: "essential-cookies",
      title: "Essential Cookies",
      icon: Zap,
      content: "Essential cookies are crucial to your experience of a website, enabling core features like user logins, account management, shopping carts, and payment processing.",
      footer: "We use essential cookies to enable certain functions on our website."
    },
    {
      id: "performance-cookies", 
      title: "Performance Cookies",
      icon: Eye,
      content: "Performance cookies track how you use a website during your visit. Typically, this information is anonymous and aggregated, with information tracked across all site users. They help companies understand visitor usage patterns, identify and diagnose problems or errors their users may encounter, and make better strategic decisions in improving their audience's overall website experience.",
      additional: "These cookies may be set by the website you're visiting (first-party) or by third-party services. They do not collect personal information about you.",
      footer: "We use performance cookies on our site."
    },
    {
      id: "functionality-cookies",
      title: "Functionality Cookies", 
      icon: Settings,
      content: "Functionality cookies are used to collect information about your device and any settings you may configure on the website you're visiting (like language and time zone settings). With this information, websites can provide you with customised, enhanced, or optimised content and services.",
      additional: "These cookies may be set by the website you're visiting (first-party) or by third-party services.",
      footer: "We do not use this type of cookie on our site."
    },
    {
      id: "targeting-cookies",
      title: "Targeting/Advertising Cookies",
      icon: Target,
      content: "Targeting/advertising cookies help determine what promotional content is most relevant and appropriate to you and your interests. Websites may use them to deliver targeted advertising or limit the number of times you see an advertisement. This helps companies improve the effectiveness of their campaigns and the quality of content presented to you.",
      additional: "These cookies may be set by the website you're visiting (first-party) or by third-party services. Targeting/advertising cookies set by third parties may be used to track you on other websites that use the same third-party service.",
      footer: "We do not use this type of cookie on our site."
    },
    {
      id: "control-cookies",
      title: "How Can You Control Our Website's Use of Cookies?",
      icon: Shield,
      content: "You have the right to decide whether to accept or reject cookies on our website. Please note that essential cookies cannot be rejected as they are strictly necessary to provide you with the services on our website.",
      additional: "You may be able to set or amend your cookie preferences by managing your web browser settings. As each web browser is different, please consult the instructions provided by your web browser (typically in the 'help' section).",
      footer: "If you choose to refuse or disable cookies, you may still use the website, though some functionality of the website may not be available to you."
    },
    {
      id: "updates",
      title: "How Often Will We Update This Cookie Policy?",
      icon: Clock,
      content: "We may update this Cookie Policy from time to time to reflect any changes to the cookies and related technologies we use, or for other operational, legal, or regulatory reasons.",
      footer: "Each time you use our website, the current version of the Cookie Policy will apply. When you use our website, you should check the date of this Cookie Policy (which appears at the top of this document) and review any changes since the last version."
    },
    {
      id: "further-info",
      title: "Where Can You Obtain Further Information?",
      icon: HelpCircle,
      content: "For any questions or concerns regarding our Cookie Policy, you may contact us using the following details:",
      footer: "Smart AI Solutions Tech Ltd - Email: info@smartaisolutions.ai"
    }
  ];

  return (
    <div className="min-h-screen bg-white font-sans">
      <Navigation />
      
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-[#112d56] via-[#1a3d6b] to-[#112d56] text-white py-20 lg:py-32">
        <div className="absolute inset-0 bg-gradient-to-br from-white/5 to-transparent"></div>
        <div className="relative container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <div className="flex justify-center mb-8">
              <div className="w-20 h-20 bg-gradient-to-br from-[#ff7a1f]/20 to-[#ff7a1f]/5 rounded-2xl flex items-center justify-center">
                <Cookie className="h-10 w-10 text-[#ff7a1f]" />
              </div>
            </div>
            <h1 className="text-4xl lg:text-6xl font-bold leading-tight tracking-tight mb-6">
              Cookie <span className="text-[#ff7a1f]">Policy</span>
            </h1>
            <p className="text-xl lg:text-2xl text-blue-100 leading-relaxed mb-8">
              We use cookies to help improve your experience of Smart AI Solutions websites and services.
            </p>
            <div className="inline-flex items-center space-x-2 bg-white/10 backdrop-blur-sm rounded-lg px-4 py-2">
              <Clock className="h-5 w-5 text-[#ff7a1f]" />
              <span className="text-blue-200">Effective Date: 21st January 2025</span>
            </div>
          </div>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <Card className="border-0 shadow-xl bg-white">
              <CardHeader className="text-center pb-6">
                <CardTitle className="text-2xl font-bold text-[#112d56] flex items-center justify-center space-x-3">
                  <Globe className="h-6 w-6 text-[#ff7a1f]" />
                  <span>About This Policy</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-gray-700 text-lg leading-relaxed">
                  This cookie policy is part of Smart AI Solutions Tech Ltd's privacy policy. It covers the use of cookies between your device and our site.
                </p>
                <p className="text-gray-700 leading-relaxed">
                  We also provide basic information on third-party services we may use, which may also use cookies as part of their service. This policy does not cover their cookies.
                </p>
                <div className="p-4 bg-orange-50 rounded-lg border-l-4 border-[#ff7a1f]">
                  <p className="text-gray-700 font-medium">
                    If you don't wish to accept cookies from us, you should instruct your browser to refuse cookies from Smart AI Solutions websites. In such a case, we may be unable to provide you with some of your desired content and services.
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Cookie Types Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl lg:text-5xl font-bold text-[#112d56] mb-6">
                Types of Cookies and <span className="text-[#ff7a1f]">How We Use Them</span>
              </h2>
              <p className="text-xl text-gray-600">
                Understanding the different types of cookies we use on our website.
              </p>
            </div>

            <div className="space-y-8">
              {sections.map((section, index) => (
                <Card key={section.id} className="border-0 shadow-lg hover:shadow-xl transition-all duration-300">
                  <CardHeader className="pb-4">
                    <CardTitle className="text-xl font-bold text-[#112d56] flex items-center space-x-3">
                      <div className="w-10 h-10 bg-gradient-to-br from-[#ff7a1f]/20 to-[#ff7a1f]/5 rounded-lg flex items-center justify-center">
                        <section.icon className="h-5 w-5 text-[#ff7a1f]" />
                      </div>
                      <span>{section.title}</span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <p className="text-gray-700 leading-relaxed">
                      {section.content}
                    </p>
                    
                    {section.additional && (
                      <p className="text-gray-700 leading-relaxed">
                        {section.additional}
                      </p>
                    )}
                    
                    {section.list && (
                      <ul className="space-y-2 ml-4">
                        {section.list.map((item, idx) => (
                          <li key={idx} className="flex items-start space-x-3">
                            <div className="w-2 h-2 bg-[#ff7a1f] rounded-full mt-2 flex-shrink-0"></div>
                            <span className="text-gray-700">{item}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                    
                    {section.footer && (
                      <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                        <p className="text-gray-700 font-medium">
                          {section.footer}
                        </p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="py-20 bg-gradient-to-r from-[#112d56] to-[#1a3d6b] text-white">
        <div className="container mx-auto px-6 text-center">
          <div className="max-w-4xl mx-auto space-y-8">
            <h2 className="text-4xl lg:text-5xl font-bold leading-tight">
              Questions About <span className="text-[#ff7a1f]">Cookies</span>?
            </h2>
            <p className="text-xl lg:text-2xl text-blue-100 leading-relaxed">
              We're here to help you understand how cookies work on our website and how you can manage your preferences.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-6 justify-center items-center pt-8">
              <Button 
                size="lg"
                className="bg-[#ff7a1f] hover:bg-[#e6681b] text-white font-bold px-12 py-6 text-xl rounded-xl transition-all duration-300 transform hover:scale-105 hover:shadow-2xl"
                onClick={() => window.location.href = 'mailto:info@smartaisolutions.ai'}
              >
                <Mail className="mr-3 h-6 w-6" />
                Contact Us About Cookies
              </Button>
            </div>
          </div>
        </div>
      </section>

      <LegalFooter />
    </div>
  );
};

export default CookiePage;